import { createAsyncThunk } from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import FraudElementsAPI, {
  CreateFraudElementsParams,
  CreateFraudElementsResponse,
  DiscardGeneratedFraudElementsParams,
  DownloadFraudElementsParams,
  FraudElementsResponse,
  GenerateFraudElementsParams,
  GeneratedFraudElementsResponse,
  GetGeneratedFraudElementsParams,
  MessageResponse,
  UpdateFraudElementParams,
  UpsertFraudElementObservationParams,
} from "../../../api/fraudElements";

import { saveBlob } from "../../../lib/utils";
import { showErrorToast } from "../../../lib/toast";

export const fetchFraudElements = createAsyncThunk<
  FraudElementsResponse,
  string,
  { state: RootState; rejectValue: string }
>(
  "fraudElements/fetchFraudElements",
  async (caseId, { rejectWithValue }) => {
    try {
      return await FraudElementsAPI.getFraudElements({ case_id: caseId });
    } catch {
      return rejectWithValue("Failed to fetch fraud elements");
    }
  },
  {
    condition(caseId, { getState }) {
      const fraudElementsState = getState().fraudElements;

      if (fraudElementsState.caseId === caseId && 
         (fraudElementsState.status === "loading" || fraudElementsState.status === "success")
      ) {
        return false;
      }

      return true;
    },
  },
);

export const createFraudElements = createAsyncThunk<
  CreateFraudElementsResponse,
  CreateFraudElementsParams,
  { rejectValue: string }
>(
  "fraudElements/createFraudElements",
  async (params, { rejectWithValue }) => {
    try {
      return await FraudElementsAPI.createFraudElements(params);
    } catch {
      return rejectWithValue("Failed to create fraud elements");
    }
  },
);

export const updateFraudElement = createAsyncThunk<
  MessageResponse,
  UpdateFraudElementParams,
  { rejectValue: string }
>(
  "fraudElements/updateFraudElement",
  async (params, { rejectWithValue }) => {
    try {
      return await FraudElementsAPI.updateFraudElement(params);
    } catch {
      return rejectWithValue("Failed to update fraud element");
    }
  },
);

export const upsertFraudElementObservation = createAsyncThunk<
  MessageResponse,
  UpsertFraudElementObservationParams,
  { rejectValue: string }
>(
  "fraudElements/upsertFraudElementObservation",
  async (params, { rejectWithValue }) => {
    try {
      return await FraudElementsAPI.upsertFraudElementObservation(params);
    } catch {
      return rejectWithValue("Failed to update observation");
    }
  },
);


export const downloadFraudElements = createAsyncThunk<
  void,
  DownloadFraudElementsParams
>(
  "fraudElements/downloadFraudElements",
  async (params) => {
    try {
      const { blob, fileName } =
        await FraudElementsAPI.downloadFraudElements(params);
      saveBlob(blob, fileName);
    } catch (error) {
      console.error(error);
      showErrorToast("Could not start the download");
    }
  },
);

export const generateFraudElements = createAsyncThunk<
  GeneratedFraudElementsResponse,
  GenerateFraudElementsParams,
  { rejectValue: string }
>(
  "fraudElements/generateFraudElements",
  async (params, { rejectWithValue }) => {
    try {
      return await FraudElementsAPI.generateFraudElements(params);
    } catch {
      return rejectWithValue("Failed to generate fraud elements");
    }
  },
);

export const getGeneratedFraudElements = createAsyncThunk<
  GeneratedFraudElementsResponse,
  GetGeneratedFraudElementsParams,
  { rejectValue: string }
>(
  "fraudElements/getGeneratedFraudElements",
  async (params, { rejectWithValue }) => {
    try {
      return await FraudElementsAPI.getGeneratedFraudElements(params);
    } catch {
      return rejectWithValue("Failed to fetch generated fraud elements");
    }
  },
);

export const discardGeneratedFraudElements = createAsyncThunk<
  MessageResponse,
  DiscardGeneratedFraudElementsParams,
  { rejectValue: string }
>(
  "fraudElements/discardGeneratedFraudElements",
  async (params, { rejectWithValue }) => {
    try {
      return await FraudElementsAPI.discardGeneratedFraudElements(params);
    } catch {
      return rejectWithValue("Failed to discard generated fraud elements");
    }
  },
);